/**
 * Shapes and converters for a coverage/option as the API actually returns it — optional collections and
 * the backend category contract (`categoryCode`/`categoryName`). Shared by every endpoint that returns a
 * coverage or an option: the coverage CRUD endpoints and the template-application endpoints alike.
 */
import { ClinicClass, CoverageCategory, CoverageLimit, CoverageOption, CoverageSubOption } from './coverage-option.model';
import { PackageContent, Pricing, ageGroupCoefficient, ageGroupPrice, buildDefaultPricing } from './package.model';

export interface CoverageResponse {
  id: string;
  pricing?: PricingResponse | null;
  options?: CoverageOptionResponse[] | null;
}

export interface CoverageOptionResponse {
  id: string;
  groupName?: string | null;
  name: string;
  description?: string | null;
  limits?: CoverageLimit[] | null;
  clinicClasses?: ClinicClass[] | null;
  diagnoses?: string[] | null;
  categories?: CoverageCategoryResponse[] | null;
  subOptions?: CoverageSubOptionResponse[] | null;
}

export interface CoverageSubOptionResponse {
  id: string;
  groupName?: string | null;
  name: string;
  description?: string | null;
  limits?: CoverageLimit[] | null;
  categories?: CoverageCategoryResponse[] | null;
}

/** Pricing as the API stores it: whole-грн base cost and a coefficient per age group. Null values may be omitted from the response. */
export interface PricingResponse {
  basePolicyCost?: number | null;
  insuredSum?: number | null;
  ageCoefficients: { minAge: number; maxAge: number; coefficient?: number | null }[];
}

export interface CoverageCategoryResponse {
  categoryCode: number;
  categoryName: string;
}

/** Normalises a coverage from the backend into the front content shape, attaching its `activeFrom`. */
export function fromCoverageResponse(coverage: CoverageResponse, activeFrom: string | null): PackageContent {
  return {
    id: coverage.id,
    activeFrom,
    pricing: fromPricingResponse(coverage.pricing),
    options: (coverage.options ?? []).map(fromOptionResponse),
  };
}

export function fromOptionResponse(option: CoverageOptionResponse): CoverageOption {
  return {
    id: option.id,
    groupName: option.groupName ?? '',
    name: option.name,
    description: option.description ?? '',
    categories: fromCategoryResponse(option.categories),
    limits: option.limits ?? [],
    clinicClasses: option.clinicClasses ?? [],
    diagnoses: (option.diagnoses ?? []).map(code => ({ code, name: '' })),
    subOptions: (option.subOptions ?? []).map(fromSubOptionResponse),
  };
}

function fromSubOptionResponse(sub: CoverageSubOptionResponse): CoverageSubOption {
  return {
    id: sub.id,
    groupName: sub.groupName ?? '',
    name: sub.name,
    description: sub.description ?? '',
    categories: fromCategoryResponse(sub.categories),
    limits: sub.limits ?? [],
  };
}

function fromCategoryResponse(categories: CoverageCategoryResponse[] | null | undefined): CoverageCategory[] {
  return (categories ?? []).map(category => ({ code: category.categoryCode, nameUa: category.categoryName }));
}

/** Maps the front dictionary category (`code`/`nameUa`) to the backend contract (`categoryCode`/`categoryName`). */
export function toCategoryPayload(category: CoverageCategory): CoverageCategoryResponse {
  return { categoryCode: category.code, categoryName: category.nameUa };
}

/**
 * Rebuilds each age group's price from the stored coefficient (rounded to kopiykas). Without a base cost no
 * price can be rebuilt, so the stored coefficient is kept on the group instead. Missing or non-numeric values
 * read as null.
 */
export function fromPricingResponse(pricing: PricingResponse | null | undefined): Pricing {
  if (!pricing) return buildDefaultPricing();
  const basePolicyCost = finiteOrNull(pricing.basePolicyCost);

  return {
    basePolicyCost,
    insuredSum: finiteOrNull(pricing.insuredSum),
    ageGroups: (pricing.ageCoefficients ?? []).map(({ minAge, maxAge, coefficient }) => {
      const stored = finiteOrNull(coefficient);
      return { minAge, maxAge, price: ageGroupPrice(basePolicyCost, stored), coefficient: stored };
    }),
  };
}

function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Converts the entered prices into the API contract: a whole-грн base cost and a coefficient per age group. */
export function toPricingPayload(pricing: Pricing): PricingResponse {
  const basePolicyCost = pricing.basePolicyCost === null ? null : Math.round(pricing.basePolicyCost);

  return {
    basePolicyCost,
    insuredSum: pricing.insuredSum,
    ageCoefficients: pricing.ageGroups.map(({ minAge, maxAge, price, coefficient }) => ({
      minAge,
      maxAge,
      coefficient: ageGroupCoefficient(price, basePolicyCost) ?? coefficient ?? 0,
    })),
  };
}
